/**
 * Camera access is user-initiated. The raw MediaStream is bound only to a
 * hidden video element that is never shown, recorded, or stored.
 */
export class CameraController {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;
  private disconnected = false;
  onDisconnected: (() => void) | null = null;

  getVideo(): HTMLVideoElement | null {
    return this.video;
  }

  isActive(): boolean {
    return Boolean(this.stream && this.video && this.video.readyState >= 2);
  }

  async enable(): Promise<HTMLVideoElement> {
    if (
      !window.isSecureContext &&
      location.hostname !== "localhost" &&
      location.hostname !== "127.0.0.1"
    ) {
      throw Object.assign(
        new Error(
          "Camera access requires a secure context. Use https:// on this machine’s local IP, or http://127.0.0.1 on this computer.",
        ),
        {
          code: "INSECURE_CONTEXT",
        },
      );
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw Object.assign(new Error("This browser does not support camera capture."), {
        code: "NO_MEDIA_DEVICES",
      });
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30, max: 30 },
        },
      });
    } catch (error) {
      throw this.mapError(error);
    }

    const video = this.createHiddenVideo();
    video.srcObject = this.stream;
    this.video = video;
    this.disconnected = false;

    for (const track of this.stream.getVideoTracks()) {
      track.addEventListener("ended", this.handleEnded);
    }

    await video.play();
    await this.waitForData(video);
    return video;
  }

  stop(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.removeEventListener("ended", this.handleEnded);
        track.stop();
      }
    }
    this.stream = null;
    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
      this.video.removeAttribute("src");
      this.video.load();
      this.video.remove();
      this.video = null;
    }
  }

  private handleEnded = (): void => {
    if (this.disconnected) return;
    this.disconnected = true;
    this.stop();
    this.onDisconnected?.();
  };

  private createHiddenVideo(): HTMLVideoElement {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute("playsinline", "true");
    video.setAttribute("aria-hidden", "true");
    video.setAttribute("disablepictureinpicture", "true");
    video.disablePictureInPicture = true;
    video.className = "raw-camera-source";
    // Fail closed even if stylesheet fails: the element is off-canvas, clipped, and non-interactive.
    video.style.cssText = [
      "position:fixed",
      "left:-10000px",
      "top:-10000px",
      "width:2px",
      "height:2px",
      "opacity:0",
      "pointer-events:none",
      "visibility:hidden",
      "overflow:hidden",
      "z-index:-9999",
      "clip-path:inset(50%)",
    ].join(";");
    document.body.appendChild(video);
    return video;
  }

  private waitForData(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(this.mapError(new Error("The camera stream could not start.")));
      };
      const cleanup = () => {
        video.removeEventListener("loadeddata", onReady);
        video.removeEventListener("error", onError);
      };
      video.addEventListener("loadeddata", onReady, { once: true });
      video.addEventListener("error", onError, { once: true });
    });
  }

  private mapError(error: unknown): Error {
    const name = error && typeof error === "object" && "name" in error ? String(error.name) : "";
    const message =
      name === "NotAllowedError"
        ? "Camera permission was denied. You can still explore in Demo Mode, or enable the camera from the browser site settings and try again."
        : name === "NotFoundError"
          ? "No camera was found. Connect a webcam or continue in Demo Mode."
          : name === "NotReadableError"
            ? "The camera is already in use by another application. Close that app and try again."
            : name === "OverconstrainedError"
              ? "The camera could not match the requested settings. Try another device or continue in Demo Mode."
              : name === "SecurityError"
                ? "The browser blocked camera access. Use https:// on the LAN address, or http://127.0.0.1 on this computer."
                : "The camera could not be started. You can continue in Demo Mode.";
    return Object.assign(new Error(message), { code: name || "CAMERA_ERROR", cause: error });
  }
}

export function describeCameraError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "The camera could not be started.";
}
